import { addDays, DayKey, diffDays, formatDayLong } from "./timeline-dates";
import { TimelineDateStyle, TimelineEntry, TimelineScale } from "./timeline.model";

export interface BarView {
  entry: TimelineEntry;
  /** 1-based column of the first and last visible day. */
  firstColumn: number;
  lastColumn: number;
  lane: number;
  /** Continues past the left / right edge of the window. */
  continuesLeft: boolean;
  continuesRight: boolean;
  /** Text shown on the bar when it isn't the entry's label (e.g. its short label on a narrow bar). */
  text?: string;
}

/**
 * Clips bars to the `days`-day window starting at `start` and packs them into lanes so overlapping bars stack.
 */
export function layoutBars(
  entries: readonly TimelineEntry[],
  start: DayKey,
  days: number
): { bars: BarView[]; lanes: number } {
  const end = addDays(start, days - 1);
  const laneEnds: number[] = [];
  const bars = entries
    .filter((entry) => entry.bar && entry.start <= end && entry.end >= start)
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : a.end < b.end ? -1 : 1))
    .map((entry) => {
      const firstColumn = diffDays(start, entry.start < start ? start : entry.start) + 1;
      const lastColumn = diffDays(start, entry.end > end ? end : entry.end) + 1;
      let lane = laneEnds.findIndex((laneEnd) => laneEnd < firstColumn);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      laneEnds[lane] = lastColumn;
      return {
        entry,
        firstColumn,
        lastColumn,
        lane,
        continuesLeft: entry.start < start,
        continuesRight: entry.end > end,
      };
    });
  return { bars, lanes: laneEnds.length };
}

/** Chip entries on `day`, in display order. */
export function chipsOn(entries: readonly TimelineEntry[], day: DayKey): TimelineEntry[] {
  return entries.filter((entry) => !entry.bar && entry.start === day).sort((a, b) => a.order - b.order);
}

/** Accessible name for a chip or bar: what it is, where, when, and whether it's part of a mismatch. */
export function entryAccessibleName(entry: TimelineEntry): string {
  const when =
    entry.bar && entry.start !== entry.end
      ? `${formatDayLong(entry.start)} to ${formatDayLong(entry.end)}`
      : formatDayLong(entry.start);
  return `${entry.description}, ${entry.context}, ${when}${entry.warn ? `, date ${entry.warn}` : ""}${entry.late ? ", late" : ""}`;
}

/** One entry placed on a packed Gantt row. */
export interface PackedItem {
  entry: TimelineEntry;
  /** Grid column of the first visible day (the label is column 1) and how many day columns it takes. */
  column: number;
  span: number;
  /** 1-based lane. */
  lane: number;
  /** Set for bars; one-day entries are drawn as a chip or a line with a pill. */
  bar: BarView | null;
  /** Text shown on the chip or pill. */
  text: string;
  /** Half the width of the first / last visible day: bars run between day centers, pills start beside the line. */
  startHalfPx: number;
  endHalfPx: number;
}

export interface PackOptions {
  scale: TimelineScale;
  dateStyle: TimelineDateStyle;
  /** Width of each day column in the window, to size one-day entries by their text and pick a bar's text. */
  dayWidths: readonly number[];
  /** Room a pill or chip takes beyond its text (padding and gaps); the date style's default when unset. */
  pillPadding?: number;
  /** Narrowest a pill or chip is drawn. */
  pillMinWidth?: number;
}

// Rough width of one-day entry text at 11px.
export function pillWidth(
  text: string,
  warn: boolean,
  options: Pick<PackOptions, "dateStyle" | "pillPadding" | "pillMinWidth">
): number {
  const padding = options.pillPadding ?? (options.dateStyle === "line" ? 22 : 28);
  return Math.max(options.pillMinWidth ?? 0, textPx(text) + (warn ? WARN_PX : 0) + padding);
}

// Rough text widths at the Gantt's 11px type, plus padding and the warning icon.
const textPx = (text: string): number => text.length * 6.2;
const WARN_PX = 14;

/**
 * A one-day entry's text: its label on the day scale, else its short label (with "Fri, Oct 30" on the week scale), or
 * its room label when `roomy`.
 */
export function pointText(entry: TimelineEntry, scale: TimelineScale, roomy = false): string {
  if (scale === "days") return entry.label;
  const short = (roomy ? entry.roomLabel : undefined) ?? entry.shortLabel ?? entry.label;
  return scale === "weeks" ? `${short} ${formatDayLong(entry.start)}` : short;
}

/**
 * Lays a row out for the week and month scales or the line date style: bars over their days, one-day entries over as
 * many days as their text needs, packed into lanes so nothing overlaps. A bar too narrow for its label shows its
 * short label.
 */
export function packRow(
  entries: readonly TimelineEntry[],
  start: DayKey,
  days: number,
  options: PackOptions
): { items: PackedItem[]; lanes: number } {
  const end = addDays(start, days - 1);
  const width = (index: number): number => options.dayWidths[index] ?? options.dayWidths.at(-1) ?? 1;
  const items = entries
    .filter((entry) => entry.start <= end && (entry.bar ? entry.end : entry.start) >= start)
    .map((entry): Omit<PackedItem, "lane"> => {
      const warnPx = entry.warn ? WARN_PX : 0;
      if (entry.bar) {
        const first = diffDays(start, entry.start < start ? start : entry.start) + 1;
        const last = diffDays(start, entry.end > end ? end : entry.end) + 1;
        const span = last - first + 1;
        // Bars run between day centers, so a bar loses half of its first and last day.
        let barPx = 0;
        for (let i = first - 1; i < last; i++) barPx += width(i);
        barPx -= (width(first - 1) + width(last - 1)) / 2;
        const fits = (text: string): boolean => textPx(text) + 18 + warnPx <= Math.max(barPx, width(first - 1));
        const shorter = [...(entry.shorterLabels ?? []), ...(entry.shortLabel ? [entry.shortLabel] : [])];
        const text = fits(entry.label) ? null : (shorter.find(fits) ?? shorter.at(-1) ?? null);
        const bar: BarView = {
          entry,
          firstColumn: first,
          lastColumn: last,
          lane: 0,
          continuesLeft: entry.start < start,
          continuesRight: entry.end > end,
          ...(text ? { text } : {}),
        };
        return {
          entry,
          column: first + 1,
          span,
          bar,
          text: bar.text ?? entry.label,
          startHalfPx: width(first - 1) / 2,
          endHalfPx: width(last - 1) / 2,
        };
      }
      const index = diffDays(start, entry.start);
      // A line style pill starts half a day in, beside its line in the middle of the day.
      const pillPx = (text: string) =>
        pillWidth(text, !!entry.warn, options) + (options.dateStyle === "line" ? width(index) / 2 : 0);
      let text = pointText(entry, options.scale);
      if (entry.roomLabel && options.scale !== "days") {
        const next = entries
          .filter((other) => other.start > entry.start && other.start <= end)
          .reduce<DayKey | null>((soonest, other) => (soonest && soonest < other.start ? soonest : other.start), null);
        const roomEnd = next ? diffDays(start, next) : days;
        let room = 0;
        for (let i = index; i < roomEnd; i++) room += width(i);
        const roomy = pointText(entry, options.scale, true);
        if (pillPx(roomy) <= room) text = roomy;
      }
      const needed = pillPx(text);
      let span = 1;
      if (options.scale !== "days") {
        for (let covered = width(index); covered < needed && index + span < days; span++)
          covered += width(index + span);
      }
      return {
        entry,
        column: index + 2,
        span,
        bar: null,
        text,
        startHalfPx: width(index) / 2,
        endHalfPx: width(index + span - 1) / 2,
      };
    })
    .sort((a, b) => a.column - b.column || b.span - a.span || a.entry.order - b.entry.order);

  const laneEnds: number[] = [];
  const placed = items.map((item): PackedItem => {
    let lane = laneEnds.findIndex((laneEnd) => laneEnd < item.column);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = item.column + item.span - 1;
    return { ...item, lane: lane + 1, ...(item.bar ? { bar: { ...item.bar, lane } } : {}) };
  });
  return { items: placed, lanes: Math.max(1, laneEnds.length) };
}
